/**
 * manifest-parser.ts — HLS m3u8 parser (RFC 8216bis-22).
 *
 * Parses two manifest types:
 *   1. Master playlist:  list of variants + audio/subs renditions
 *   2. Media playlist:   list of segments + init-map + duration-info
 *
 * What we support (F1 W3 baseline):
 *   - #EXT-X-VERSION
 *   - #EXT-X-TARGETDURATION
 *   - #EXT-X-MEDIA-SEQUENCE
 *   - #EXT-X-PLAYLIST-TYPE (VOD / EVENT)
 *   - #EXT-X-INDEPENDENT-SEGMENTS
 *   - #EXT-X-MAP (init-segment, byte-range optional)
 *   - #EXT-X-DISCONTINUITY (track segment-discontinuities)
 *   - #EXT-X-PROGRAM-DATE-TIME (DVR-anchor)
 *   - #EXT-X-ENDLIST (VOD complete)
 *   - #EXTINF + URI lines for segments
 *   - #EXT-X-STREAM-INF (variants, master)
 *   - #EXT-X-MEDIA (audio/subtitle renditions, master)
 *
 * What we DON'T support yet (F1 W3.5+):
 *   - #EXT-X-BYTERANGE (segments share a file)
 *   - #EXT-X-DATERANGE (in-stream metadata)
 *   - LL-HLS parts (#EXT-X-PART, #EXT-X-PRELOAD-HINT)
 *   - #EXT-X-KEY (DRM)
 *   - #EXT-X-IMAGE-STREAM-INF (trick-mode I-frames)
 */

// ── Master playlist types ────────────────────────────────────────────

/** Single variant in a master playlist (#EXT-X-STREAM-INF). */
export interface Variant {
  /** Resolved playlist URL (absolute or base-relative). */
  url:           string;
  /** Total bandwidth (peak) in bits/sec. */
  bandwidthBps:  number;
  /** Average bandwidth in bits/sec (optional). */
  avgBandwidthBps?: number;
  /** Comma-separated codec-strings ("avc1.640029,mp4a.40.2"). */
  codecs?:       string;
  /** Width × height as [w, h]. */
  resolution?:   readonly [number, number];
  /** Reference to audio-rendition group-id. */
  audioGroup?:   string;
  /** Reference to subtitles-rendition group-id. */
  subsGroup?:    string;
  /** Frame-rate. */
  fps?:          number;
  /** HDR-format string from #EXT-X-STREAM-INF (HDR10, HLG, dolby-vision). */
  hdrFormat?:    string;
}

/** Audio or subtitle rendition (#EXT-X-MEDIA TYPE=AUDIO|SUBTITLES). */
export interface Rendition {
  type:        'audio' | 'subtitles' | 'closed-captions' | 'video';
  /** Group-id matching variants' AUDIO=/SUBTITLES= attribute. */
  groupId:     string;
  /** Display-name. */
  name:        string;
  /** Optional URI to a media-playlist (audio: usually present; subs: always). */
  url?:        string;
  /** ISO 639-2 language code. */
  language?:   string;
  /** Default flag. */
  default:     boolean;
  /** Auto-select. */
  autoselect:  boolean;
  /** Forced (subtitles only). */
  forced:      boolean;
  /** Channel-count (audio only). */
  channels?:   number;
}

export interface MasterPlaylist {
  version:      number;
  variants:     readonly Variant[];
  renditions:   readonly Rendition[];
  independentSegments: boolean;
}

// ── Media playlist types ─────────────────────────────────────────────

/** A single segment in a media playlist. */
export interface Segment {
  /** Resolved URL. */
  url:           string;
  /** Duration in seconds (#EXTINF). */
  durationSec:   number;
  /** Sequence number (#EXT-X-MEDIA-SEQUENCE + index). */
  sn:            number;
  /** Start-time in seconds (cumulative from playlist start). */
  startTimeSec:  number;
  /** True if preceded by #EXT-X-DISCONTINUITY. */
  discontinuity: boolean;
  /** Wall-clock from #EXT-X-PROGRAM-DATE-TIME (Date or undefined). */
  programDate?:  Date;
  /** Title from optional EXTINF-comma-suffix. */
  title?:        string;
}

/** Init-segment reference (#EXT-X-MAP). */
export interface InitMap {
  url:        string;
  /** Optional byte-range: [start, length]. */
  byteRange?: readonly [number, number];
}

export interface MediaPlaylist {
  version:           number;
  type:              'VOD' | 'EVENT' | 'live';
  /** Target segment-duration (seconds, ceil). */
  targetDurationSec: number;
  /** Initial media-sequence number. */
  mediaSequence:     number;
  /** True if #EXT-X-ENDLIST present (i.e. VOD complete). */
  endlist:           boolean;
  initMap?:          InitMap;
  segments:          readonly Segment[];
  /** Total duration in seconds (sum of EXTINFs). */
  totalDurationSec:  number;
}

// ── Parser entry-points ──────────────────────────────────────────────

export class ManifestParseError extends Error {
  constructor(public readonly line: number, message: string) {
    super(`m3u8 parse error at line ${line}: ${message}`);
  }
}

/** Detect master vs media by presence of #EXT-X-STREAM-INF (master-only). */
export function isMasterPlaylist(text: string): boolean {
  for (const line of text.split('\n')) {
    if (line.startsWith('#EXT-X-STREAM-INF')) return true;
    if (line.startsWith('#EXT-X-TARGETDURATION')) return false;
  }
  return false;
}

/**
 * Parse a master playlist. `baseUrl` is used to resolve relative URIs.
 */
export function parseMasterPlaylist(text: string, baseUrl: string): MasterPlaylist {
  const lines = text.split(/\r?\n/);
  if (!lines[0].startsWith('#EXTM3U')) {
    throw new ManifestParseError(1, 'missing #EXTM3U');
  }

  let version = 1;
  let independentSegments = false;
  const variants:   Variant[]    = [];
  const renditions: Rendition[]  = [];

  let pendingStreamAttrs: ParsedAttrs | null = null;

  for (let i = 0; i < lines.length; ++i) {
    const line = lines[i].trim();
    if (line === '') continue;

    if (line.startsWith('#EXT-X-VERSION:')) {
      version = parseInt(line.substring(15), 10) || 1;
      continue;
    }
    if (line === '#EXT-X-INDEPENDENT-SEGMENTS') {
      independentSegments = true;
      continue;
    }
    if (line.startsWith('#EXT-X-MEDIA:')) {
      const attrs = parseAttrList(line.substring(13));
      const r: Rendition = {
        type:       (attrs.TYPE?.toLowerCase() as Rendition['type']) ?? 'audio',
        groupId:    attrs['GROUP-ID'] ?? '',
        name:       attrs.NAME        ?? '',
        url:        attrs.URI ? resolveUrl(attrs.URI, baseUrl) : undefined,
        language:   attrs.LANGUAGE,
        default:    attrs.DEFAULT === 'YES',
        autoselect: attrs.AUTOSELECT === 'YES',
        forced:     attrs.FORCED === 'YES',
        channels:   attrs.CHANNELS ? parseInt(attrs.CHANNELS, 10) : undefined,
      };
      renditions.push(r);
      continue;
    }
    if (line.startsWith('#EXT-X-STREAM-INF:')) {
      pendingStreamAttrs = parseAttrList(line.substring(18));
      continue;
    }
    // URI line after #EXT-X-STREAM-INF.
    if (!line.startsWith('#') && pendingStreamAttrs) {
      const a = pendingStreamAttrs;
      pendingStreamAttrs = null;
      const res = a.RESOLUTION ? parseResolution(a.RESOLUTION) : undefined;
      variants.push({
        url:               resolveUrl(line, baseUrl),
        bandwidthBps:      parseInt(a.BANDWIDTH ?? '0', 10),
        avgBandwidthBps:   a['AVERAGE-BANDWIDTH']
                             ? parseInt(a['AVERAGE-BANDWIDTH'], 10) : undefined,
        codecs:            a.CODECS,
        resolution:        res,
        audioGroup:        a.AUDIO,
        subsGroup:         a.SUBTITLES,
        fps:               a['FRAME-RATE'] ? parseFloat(a['FRAME-RATE']) : undefined,
        hdrFormat:         a['VIDEO-RANGE'],
      });
      continue;
    }
  }

  return { version, variants, renditions, independentSegments };
}

/**
 * Parse a media playlist. Returns segments with absolute URLs.
 */
export function parseMediaPlaylist(text: string, baseUrl: string): MediaPlaylist {
  const lines = text.split(/\r?\n/);
  if (!lines[0].startsWith('#EXTM3U')) {
    throw new ManifestParseError(1, 'missing #EXTM3U');
  }

  let version = 1;
  let type: MediaPlaylist['type'] = 'live';
  let targetDuration = 0;
  let mediaSequence = 0;
  let endlist = false;
  let initMap: InitMap | undefined;

  const segments: Segment[] = [];

  let pendingDuration: number  = 0;
  let pendingTitle: string | undefined;
  let pendingDiscontinuity = false;
  let pendingProgramDate: Date | undefined;
  let cumulativeStartSec = 0;
  let segmentSn = 0;

  for (let i = 0; i < lines.length; ++i) {
    const line = lines[i].trim();
    if (line === '') continue;

    if (line.startsWith('#EXT-X-VERSION:')) {
      version = parseInt(line.substring(15), 10) || 1;
      continue;
    }
    if (line.startsWith('#EXT-X-TARGETDURATION:')) {
      targetDuration = parseFloat(line.substring(22));
      continue;
    }
    if (line.startsWith('#EXT-X-MEDIA-SEQUENCE:')) {
      mediaSequence = parseInt(line.substring(22), 10) || 0;
      segmentSn = mediaSequence;
      continue;
    }
    if (line.startsWith('#EXT-X-PLAYLIST-TYPE:')) {
      const val = line.substring(21).trim();
      if (val === 'VOD' || val === 'EVENT') type = val;
      continue;
    }
    if (line === '#EXT-X-ENDLIST') {
      endlist = true;
      continue;
    }
    if (line.startsWith('#EXT-X-MAP:')) {
      const attrs = parseAttrList(line.substring(11));
      initMap = {
        url:       attrs.URI ? resolveUrl(attrs.URI, baseUrl) : '',
        byteRange: attrs.BYTERANGE ? parseByterange(attrs.BYTERANGE) : undefined,
      };
      continue;
    }
    if (line === '#EXT-X-DISCONTINUITY') {
      pendingDiscontinuity = true;
      continue;
    }
    if (line.startsWith('#EXT-X-PROGRAM-DATE-TIME:')) {
      pendingProgramDate = new Date(line.substring(25));
      continue;
    }
    if (line.startsWith('#EXTINF:')) {
      const rest = line.substring(8);
      const comma = rest.indexOf(',');
      if (comma >= 0) {
        pendingDuration = parseFloat(rest.substring(0, comma));
        pendingTitle    = rest.substring(comma + 1) || undefined;
      } else {
        pendingDuration = parseFloat(rest);
      }
      continue;
    }
    // URI line after #EXTINF.
    if (!line.startsWith('#')) {
      segments.push({
        url:           resolveUrl(line, baseUrl),
        durationSec:   pendingDuration,
        sn:            segmentSn++,
        startTimeSec:  cumulativeStartSec,
        discontinuity: pendingDiscontinuity,
        programDate:   pendingProgramDate,
        title:         pendingTitle,
      });
      cumulativeStartSec += pendingDuration;
      pendingDuration = 0;
      pendingTitle = undefined;
      pendingDiscontinuity = false;
      pendingProgramDate = undefined;
    }
  }

  return {
    version,
    type:               endlist ? 'VOD' : type,
    targetDurationSec:  targetDuration,
    mediaSequence,
    endlist,
    initMap,
    segments,
    totalDurationSec:   cumulativeStartSec,
  };
}

// ── Internal helpers ─────────────────────────────────────────────────

type ParsedAttrs = Record<string, string>;

/**
 * Parse the comma-separated attribute-list of an EXT-X-* tag value.
 * Handles quoted strings + integer + float values.
 *
 *   #EXT-X-MEDIA:TYPE=AUDIO,GROUP-ID="audio",NAME="ger",DEFAULT=YES
 */
function parseAttrList(s: string): ParsedAttrs {
  const out: ParsedAttrs = {};
  let i = 0;
  while (i < s.length) {
    // Skip whitespace + commas.
    while (i < s.length && (s[i] === ' ' || s[i] === ',')) i++;
    // Read attr name (until '=').
    const eq = s.indexOf('=', i);
    if (eq < 0) break;
    const name = s.substring(i, eq);
    i = eq + 1;
    let value: string;
    if (s[i] === '"') {
      // Quoted string.
      i++;
      const end = s.indexOf('"', i);
      if (end < 0) { value = s.substring(i); i = s.length; }
      else         { value = s.substring(i, end); i = end + 1; }
    } else {
      // Unquoted (until comma or EOL).
      let j = i;
      while (j < s.length && s[j] !== ',') j++;
      value = s.substring(i, j);
      i = j;
    }
    out[name] = value;
  }
  return out;
}

/** "1920x1080" → [1920, 1080] */
function parseResolution(s: string): readonly [number, number] | undefined {
  const m = s.match(/^(\d+)x(\d+)$/);
  if (!m) return undefined;
  return [parseInt(m[1], 10), parseInt(m[2], 10)];
}

/** "<length>[@<offset>]" → [start, length]. */
function parseByterange(s: string): readonly [number, number] | undefined {
  const at = s.indexOf('@');
  if (at < 0) return undefined;
  const length = parseInt(s.substring(0, at), 10);
  const offset = parseInt(s.substring(at + 1), 10);
  return [offset, length];
}

/** Resolve relative URI against base. */
function resolveUrl(uri: string, baseUrl: string): string {
  try {
    return new URL(uri, baseUrl).toString();
  } catch {
    return uri;
  }
}
